import { FiCopy, FiLink, FiLock } from "react-icons/fi";
import { useState, useEffect } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";

const APP = "https://app.clipbait.ai";

const C = {
  brand: "#ff6a00",
  brand2: "#ff8a33",
  line: "rgba(255,255,255,0.10)",
  lineSoft: "rgba(255,255,255,0.07)",
  panel: "rgba(255,255,255,0.035)",
  panel2: "rgba(255,255,255,0.06)",
  txt: "rgba(255,255,255,0.95)",
  dim: "rgba(255,255,255,0.78)",
  dim2: "rgba(255,255,255,0.62)",
  code: "#ffb87a",
  green: "#3ddc97",
};

const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const ghostBtn = {
  background: C.panel2,
  border: `1px solid ${C.line}`,
  color: C.txt,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 9,
  cursor: "pointer",
  whiteSpace: "nowrap",
  transition: "background .15s, border-color .15s",
};

const primaryBtn = {
  background: `linear-gradient(135deg, ${C.brand}, ${C.brand2})`,
  color: "#fff",
  border: "none",
  borderRadius: 999,
  padding: "12px 22px",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
};

// Ways to connect. The "paste a link" custom-connector flow is the easiest and
// the default — no install, works in Claude and ChatGPT.
const AGENTS = [
  { id: "connector", icon: <FiLink />, title: "Claude or ChatGPT", subtitle: "Easiest. Just paste a link, no install.", cmd: (k) => `${APP}/api/mcp/${k}`,
    hint: "In Claude: Settings → Connectors → Add custom connector → paste this link. In ChatGPT: Settings → Connectors → add an MCP server with this link." },
  { id: "claude-code", icon: "◈", title: "Claude Code", subtitle: "One command in your terminal", cmd: (k) => `npx clipbait@latest ${k}` },
  { id: "cli", icon: "❯", title: "Command line", subtitle: "Make clips from your terminal", cmd: (k) => `npx clipbait@latest login ${k}` },
  { id: "api", icon: "{ }", title: "Direct API", subtitle: "curl or any HTTP client", cmd: (k) => `curl -H "X-API-Key: ${k}" ${APP}/api/clips/jobs` },
];

function AgentBadge({ icon }) {
  return (
    <span
      style={{
        display: "grid",
        placeItems: "center",
        width: 30,
        height: 30,
        borderRadius: 8,
        background: "rgba(255,106,0,0.14)",
        color: C.brand2,
        fontFamily: mono,
        fontSize: 14,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {icon}
    </span>
  );
}

export default function DeveloperApiSection() {
  const [key, setKey] = useState(null); // null = loading, "" = none yet, string = the key
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    axiosInstance
      .get("/users/me/api-key")
      .then((r) => setKey(r.data?.apiKey || ""))
      .catch(() => setKey(""));
  }, []);

  const generate = async (rotate) => {
    if (rotate && !window.confirm("Make a new key? Anything connected with the old key will stop working until you paste in the new one.")) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post("/users/me/api-key");
      setKey(r.data.apiKey);
      setRevealed(true);
      toast.success(rotate ? "New API key generated" : "API key created");
    } catch (e) {
      toast.error(e.response?.data?.error || "Couldn't create key");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed, select it manually");
    }
  };

  const masked = key ? `${key.slice(0, 12)}${"•".repeat(14)}${key.slice(-4)}` : "";
  // OAuth handles auth, so the connect link is the same bare URL for everyone
  // (no key in it). Pasting it into Claude triggers a sign-in + approve screen.
  const mcpLink = `${APP}/api/mcp`;
  const advancedAgents = AGENTS.filter((a) => a.id !== "connector");

  return (
    <div className="me-section">
      <h3 className="me-section-title">
        Use Clipbait with AI
        <span
          style={{
            marginLeft: 8,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.3,
            color: C.brand2,
            border: "1px solid rgba(255,106,0,0.5)",
            borderRadius: 999,
            padding: "2px 9px",
            verticalAlign: "middle",
          }}
        >
          NEW
        </span>
      </h3>
      <p className="me-section-desc" style={{ color: C.dim }}>
        Ask Claude or ChatGPT to make clips for you.{" "}
        <a href={`${APP}/docs`} target="_blank" rel="noopener noreferrer" style={{ color: C.brand2, fontWeight: 700, whiteSpace: "nowrap" }}>
          Developer docs →
        </a>
      </p>

      {/* ---- Primary: connect via the bare MCP link. Works for everyone via
              OAuth — no key needed, so this is all most users ever see. ---- */}
      <div style={{ fontSize: 15, fontWeight: 700, color: C.txt, marginBottom: 6 }}>Connect to Claude or ChatGPT</div>
      <div style={{ fontSize: 13.5, color: C.txt, marginBottom: 14, lineHeight: 1.55 }}>
        Copy the link, then in Claude: <b>Settings → Connectors → Add custom connector</b> → paste. You'll sign in to Clipbait once to approve it. Done.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => copy(mcpLink, "MCP link")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: `linear-gradient(135deg, ${C.brand}, ${C.brand2})`,
            color: "#fff",
            border: "none",
            borderRadius: 999,
            padding: "13px 24px",
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <FiCopy /> Copy MCP link
        </button>
        <span style={{ fontSize: 12, color: C.dim2, maxWidth: 300 }}>
          No key needed. Nothing secret in the link, so it's safe to share or screen record.
        </span>
      </div>

      {/* ---- Developer options (API key, CLI, direct API) tucked away ---- */}
      <button
        onClick={() => setShowAdvanced((v) => !v)}
        style={{ marginTop: 18, background: "none", border: "none", color: C.dim, fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <span style={{ display: "inline-block", transform: showAdvanced ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▸</span>
        For developers: API key, CLI, direct API
      </button>

      {showAdvanced && (
        <div style={{ marginTop: 12 }}>
          {/* API key */}
          {key === null ? (
            <p style={{ color: C.dim }}>Loading…</p>
          ) : !key ? (
            <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 14, padding: 18 }}>
              <div style={{ fontSize: 13.5, color: C.dim, marginBottom: 12 }}>An API key is only needed for the CLI or direct API calls (not for the Claude/ChatGPT connector above).</div>
              <button style={{ ...primaryBtn, opacity: busy ? 0.7 : 1 }} onClick={() => generate(false)} disabled={busy}>
                {busy ? "Generating…" : "Generate API key"}
              </button>
            </div>
          ) : (
            <div style={{ background: `linear-gradient(180deg, rgba(255,106,0,0.05), rgba(255,255,255,0.02))`, border: `1px solid ${C.line}`, borderRadius: 14, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: C.green, boxShadow: `0 0 8px ${C.green}` }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: C.dim, textTransform: "uppercase", letterSpacing: 0.6 }}>API key (for CLI / direct API)</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <code style={{ flex: "1 1 260px", minWidth: 0, background: "rgba(0,0,0,0.4)", border: `1px solid ${C.lineSoft}`, borderRadius: 10, padding: "12px 14px", fontFamily: mono, fontSize: 14, letterSpacing: revealed ? 0 : 1, color: "#fff", overflowX: "auto", whiteSpace: "nowrap" }}>
                  {revealed ? key : masked}
                </code>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={ghostBtn} onClick={() => setRevealed((v) => !v)}>{revealed ? "Hide" : "Reveal"}</button>
                  <button style={ghostBtn} onClick={() => copy(key, "API key")}>Copy</button>
                  <button style={{ ...ghostBtn, color: C.dim }} onClick={() => generate(true)} disabled={busy} title="Make a new key. The old one stops working.">New key</button>
                </div>
              </div>
              <p style={{ margin: "12px 0 0", fontSize: 12, color: C.dim2 }}><FiLock /> Keep this private, like a password. You can make a new one anytime.</p>
            </div>
          )}

          {/* CLI / direct API copy rows */}
          {key && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {advancedAgents.map((a) => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10, padding: "10px 12px" }}>
                  <AgentBadge icon={a.icon} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.txt }}>{a.title}</div>
                    <div style={{ fontSize: 12, color: C.dim2 }}>{a.subtitle}</div>
                  </div>
                  <button
                    onClick={() => copy(a.cmd(key), a.title)}
                    style={{ background: `linear-gradient(135deg, ${C.brand}, ${C.brand2})`, color: "#fff", border: "none", borderRadius: 999, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    Copy
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
